import { TeamName } from "@/components/TeamName";
import {
  getAppointmentStatusIcon,
  isAssociatedWithProject,
} from "@/features/appointments/utils";
import { getAppointmentTitle } from "@/features/calendar/utils";
import { useModals } from "@/features/modals/hooks";
import { ProjectQuickInfo } from "@/features/project/ProjectQuickInfo";
import {
  Accordion,
  AccordionItem,
  Badge,
  Button,
  InlineMessage,
  ListView,
  ListViewItem,
  Loading,
  Text,
  Variables,
  calendarIcon,
  clockIcon,
  locationPinIcon,
  personIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  CustomerCardWrapper,
  ElementsContainer,
  FlexDiv,
  OneLineInfo,
  SectionContainer,
  translateAppointmentType,
  translateLocationType,
} from "@repo/ui";
import isEmpty from "lodash/isEmpty";

import { AppointmentDetails } from "../../AppointmentDetails";
import { AppointmentStatusIndicator } from "../../AppointmentStatusIndicator";
import { AppointmentEditButtons } from "./AppointmentEditButtons";
import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
} from "./AppointmentModalReducer";
import { AppointmentTime } from "./AppointmentTime";

const MissingOpportunityInformation = ({
  state,
}: {
  state: AppointmentModalState;
}) => {
  if (state.appointment?.reasonCode !== undefined) {
    return (
      <InlineMessage
        title={t("prompt.no_project_created")}
        body={t(`reason_code.${state.appointment?.reasonCode}`)}
        variant="cautionary"
      />
    );
  }
  return null;
};

export const CalendarAppointmentView = ({
  state,
  actions,
  onDelete,
  isLoadingDetails,
}: {
  state: AppointmentModalState;
  actions: AppointmentModalStateActions;
  onDelete: () => void;
  isLoadingDetails?: boolean;
}) => {
  const { pushModal } = useModals();

  const hasOpportunity = isAssociatedWithProject(state.opportunityId);
  const customerTimezone = state.customer?.timezone;

  if (!state.appointment) {
    return <Loading text={t("prompt.loading")} />;
  }

  return (
    <SectionContainer>
      <ElementsContainer>
        <FlexDiv justifyContent="space-between" gap={`${Variables.space50}`}>
          <Text textStyle="Heading.M">
            {getAppointmentTitle(state.appointment)}
          </Text>
          <AppointmentEditButtons
            state={state}
            actions={actions}
            onEdit={() => {
              actions.setStep("EDIT");
            }}
            isLoadingDetails={isLoadingDetails}
            onDelete={() =>
              pushModal({
                type: "CONFIRM",
                onConfirm: onDelete,
                title: t("action.delete_appointment"),
                message: t("prompt.confirm_delete_appointment"),
              })
            }
          />
        </FlexDiv>
        {state.appointment.appointmentType && (
          <div>
            <Badge
              colour={
                state.appointment.appointmentType === "WALKIN_BOOKING"
                  ? "extendedPalette19"
                  : "blue"
              }
              label={translateAppointmentType(
                state.appointment.appointmentType
              )}
              size="small"
            />
          </div>
        )}

        <ListView id="calendar-appointment-details" showDivider={false}>
          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.scheduled_time")}
            description={
              state.appointment.timeWindowStartTime ? (
                <AppointmentTime
                  variant="MANAGE"
                  status={state.appointment.appointmentStatus}
                  startTime={state.appointment.timeWindowStartTime}
                  endTime={state.appointment.timeWindowEndTime}
                  customerTimezone={customerTimezone}
                />
              ) : (
                "-"
              )
            }
          />
          <ListViewItem
            leadingIcon={clockIcon}
            title={t("label.appointment_time")}
            description={
              state.appointment.actualTimeWindowStartTime ? (
                <AppointmentTime
                  variant="MANAGE"
                  status={state.appointment.appointmentStatus}
                  startTime={state.appointment.actualTimeWindowStartTime}
                  endTime={state.appointment.actualTimeWindowEndTime}
                  customerTimezone={customerTimezone}
                />
              ) : (
                "-"
              )
            }
          />

          <ListViewItem
            leadingIcon={locationPinIcon}
            title={t("label.location")}
            description={
              <OneLineInfo
                info={[
                  state.appointment.locationName,
                  translateLocationType(state.appointment.locationType),
                ]}
                divider=" - "
              />
            }
          />
          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.assigned_to")}
            description={
              !isEmpty(state.appointment.assignedCoworker) ? (
                <CoWorkerName email={state.appointment.assignedCoworker} />
              ) : state.appointment?.assignedTeam ? (
                <TeamName teamId={state.appointment.assignedTeam} />
              ) : (
                t("label.unassigned")
              )
            }
          />
          <ListViewItem
            leadingIcon={getAppointmentStatusIcon(
              state.appointment.appointmentStatus
            )}
            title={t("label.status")}
            description={
              <AppointmentStatusIndicator
                status={state.appointment.appointmentStatus}
              />
            }
          />
        </ListView>
        {state.appointment.videoLinkCoworker && (
          <Button
            size="small"
            type="emphasised"
            href={state.appointment.videoLinkCoworker}
            newWindow={true}
            text={t("action.join_online_meeting")}
          />
        )}
      </ElementsContainer>

      <Accordion collapsible={false}>
        <AccordionItem
          id="customer-details"
          title={t("label.customer_details")}
          open={true}
        >
          <CustomerCardWrapper
            editable={false}
            projectId={state.opportunityId}
            orderId={state.appointment.orderNumber}
            isAppointmentOrder={true}
            setCustomer={actions.setCustomer}
          />
        </AccordionItem>

        {state.appointment && (
          <AccordionItem
            id="appointment-details"
            title={t("label.appointment_details")}
            open={true}
          >
            <AppointmentDetails appointment={state.appointment} />
          </AccordionItem>
        )}
        {hasOpportunity && (
          <ProjectQuickInfo projectId={state.opportunityId} showTabs />
        )}
      </Accordion>

      {!hasOpportunity && <MissingOpportunityInformation state={state} />}
    </SectionContainer>
  );
};
